import React from 'react';
import { View, Switch, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface SettingsToggleItemProps {
  label: string;
  icon: React.ReactNode;
  iconBgColor?: string;
  iconBorderColor?: string;
  value: boolean;
  onValueChange: (value: boolean) => void;
  disabled?: boolean;
  showSeparator?: boolean;
}

export const SettingsToggleItem: React.FC<SettingsToggleItemProps> = React.memo(({
  label,
  icon,
  iconBgColor,
  iconBorderColor,
  value,
  onValueChange,
  disabled = false,
  showSeparator = true,
}) => {
  const { colors, mode } = useThemeColors();
  const defaultIconBg = iconBgColor ?? colors.activityIcon.bg;
  const defaultIconBorder = iconBorderColor ?? colors.activityIcon.border;

  const trackOn = 'rgba(70,114,254,0.35)';
  const trackOff = mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const thumbOff = mode === 'dark' ? '#555' : '#CCCCCC';

  return (
    <View style={[styles.row, showSeparator && { borderBottomWidth: 1, borderBottomColor: colors.separator }]}>
      <View
        style={[
          styles.iconContainer,
          { backgroundColor: defaultIconBg, borderColor: defaultIconBorder },
        ]}
      >
        {icon}
      </View>
      <Text style={[styles.label, { color: colors.text.tertiary }]} numberOfLines={2}>
        {label}
      </Text>
      <Switch
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        trackColor={{
          false: trackOff,
          true: trackOn,
        }}
        thumbColor={value ? PRIMITIVE_COLORS.resioBlue : thumbOff}
        ios_backgroundColor={trackOff}
        style={Platform.OS === 'web' ? styles.switchWeb : undefined}
      />
    </View>
  );
});

SettingsToggleItem.displayName = 'SettingsToggleItem';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    // 69 = 36pt icon + 2×16 padding (+1 of slack); grows with a two-line label.
    minHeight: 69,
    paddingVertical: 16,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  label: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontWeight: '500',
    flex: 1,
  },
  switchWeb: {
    height: 26,
  },
});
